import {useCallback, useEffect, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {useDispatch, useSelector} from '../../state/hooks';
import * as PollingActions from '../../state/polling/actions';
import {clearSlowEvents} from '../../state/polling/slowEvents';
import {usePart} from '../../state/views/hooks';
import {type Ref as PanelRef} from '../../state/views/panel/types';
import type {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {usePanelIsActive} from '../../util/usePanelIsActive';
import {getTrimmedTitleFromAnyConfig} from '../PanelTitle/getTitleFromConfig';
import {getSharePanelLink} from './FullScreenPanel/fullScreenUtils';

export const useNextAndPreviousPanel = ({
  panelBankSectionConfigRef,
  panelRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigRef;
  panelRef: PanelRef;
}) => {
  const dispatch = useDispatch();
  // This hook only ever triggers ticks, it never listens for them, so it
  // dispatches directly rather than going through a polling subscriber hook
  // that would subscribe every panel to polling state.
  const tick = useCallback(() => {
    dispatch(PollingActions.tick());
    clearSlowEvents();
  }, [dispatch]);
  const history = useHistory();
  const location = useLocation();
  const sectionPanelRefs = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].panelRefs
  );
  const currIndex = sectionPanelRefs.findIndex(ref => ref.id === panelRef.id);

  const panels = useSelector(state =>
    sectionPanelRefs.map(ref => state.views.parts[ref.type][ref.id])
  );

  const sectionName = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].name
  );

  const section = usePart(panelBankSectionConfigRef);
  const panelIsActive = usePanelIsActive();

  const goToPanel = useCallback(
    (index: number, direction: 'next' | 'previous') => {
      let targetIndex = index;
      while (targetIndex >= 0 && targetIndex < panels.length) {
        const targetPanel = panels[targetIndex];
        const isPanelActive = panelIsActive({
          section: {type: section.type, name: sectionName} as any,
          panel: targetPanel,
        });

        if (isPanelActive) {
          // Force a new query by triggering a tick
          tick();
          // Navigate to the new panel
          history.push(getSharePanelLink(targetPanel, sectionName, location));
          return;
        }
        targetIndex += direction === 'next' ? 1 : -1;
      }
    },
    [panels, section, tick, history, location, sectionName, panelIsActive]
  );

  const goToPreviousPanel = useCallback(
    () => goToPanel(currIndex - 1, 'previous'),
    [goToPanel, currIndex]
  );

  const goToNextPanel = useCallback(
    () => goToPanel(currIndex + 1, 'next'),
    [goToPanel, currIndex]
  );

  const {hasPreviousPanel, hasNextPanel, previousPanelTitle, nextPanelTitle} =
    useMemo(() => {
      const findActivePanel = (
        startIndex: number,
        increment: number,
        endCondition: (i: number) => boolean
      ) => {
        for (let i = startIndex; endCondition(i); i += increment) {
          const panel = panels[i];
          const isPanelActive = panelIsActive({
            section: {type: section.type, name: sectionName} as any,
            panel: panel,
          });
          if (isPanelActive) {
            return {
              hasPanel: true,
              title:
                getTrimmedTitleFromAnyConfig(panel.viewType, panel) ||
                (increment > 0 ? 'Next Panel' : 'Previous Panel'),
            };
          }
        }
        return {
          hasPanel: false,
          title: increment > 0 ? 'Next Panel' : 'Previous Panel',
        };
      };

      const previous = findActivePanel(currIndex - 1, -1, i => i >= 0);
      const next = findActivePanel(currIndex + 1, 1, i => i < panels.length);

      return {
        hasPreviousPanel: previous.hasPanel,
        hasNextPanel: next.hasPanel,
        previousPanelTitle: previous.title,
        nextPanelTitle: next.title,
      };
    }, [currIndex, panels, section, sectionName, panelIsActive]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if no input elements are focused
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (hasPreviousPanel && e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        goToPreviousPanel();
      } else if (hasNextPanel && e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        goToNextPanel();
      }
    };

    // TODO: migrate panel prev/next (Arrow keys) to the inputSystem
    // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPreviousPanel, hasNextPanel, goToPreviousPanel, goToNextPanel]);

  return {
    goToPreviousPanel,
    goToNextPanel,
    hasPreviousPanel,
    hasNextPanel,
    previousPanelTitle,
    nextPanelTitle,
  };
};
